import {
  V1GenerationErrorKind,
  type V1GenerationFailed,
} from '@/generated/hooks/rain/models';

/**
 * The backend surfaces the tool-loop iteration cap as a terminal
 * `GenerationFailed` event: `kind = internal_error`, `retryable = false`, and a
 * message of the form `Internal error: Tool loop reached maximum iterations (N)`
 * (MAN-3769). The backend already re-arms the loop for another batch when the
 * user chooses to continue, so this is a soft limit the UI turns into a
 * question rather than a hard error.
 *
 * `kind` + `retryable` alone don't uniquely identify this failure — a genuine
 * internal server error carries the same pair — so we also match the message
 * text. If the backend ever promotes this to a dedicated error kind, matching
 * on the kind alone becomes safe and this regexp can be dropped.
 */
const MAX_ITERATIONS_MESSAGE_RE = /tool loop reached maximum iterations/i;

// Pulls the iteration count out of the "…maximum iterations (N)" message so the
// card can tell the user how many turns they've hit. Undefined when the count
// is absent or unparseable — the card falls back to a countless copy.
const MAX_ITERATIONS_COUNT_RE = /maximum iterations\s*\((\d+)\)/i;

export const isMaxIterationsFailure = (
  failure: V1GenerationFailed | undefined,
): boolean => {
  if (!failure) return false;
  if (failure.retryable === true) return false;
  if (
    failure.kind !== V1GenerationErrorKind.GENERATION_ERROR_KIND_INTERNAL_ERROR
  ) {
    return false;
  }
  return MAX_ITERATIONS_MESSAGE_RE.test(failure.message ?? '');
};

export const parseMaxIterationsCount = (
  message: string | undefined,
): number | undefined => {
  const match = (message ?? '').match(MAX_ITERATIONS_COUNT_RE);
  if (!match) return undefined;
  const count = Number.parseInt(match[1], 10);
  return Number.isFinite(count) ? count : undefined;
};

// Agent-facing replies sent when the user resolves the max-iterations card.
// These are plain instructions the agent reads to decide whether to keep
// building or to hand control back to the user. They are kept verbatim in sync
// with the English button labels (`rain.maxIterations.continue`
// / `.breakDown`) so what the user picks is exactly what the agent receives.
export const MAX_ITERATIONS_CONTINUE_MESSAGE = 'Yes, continue working on it.';
export const MAX_ITERATIONS_STOP_MESSAGE = "No, I'll break down the problem.";
